import React, { useState, useCallback, memo } from 'react';
import type { ThinkingItem } from '../../types/contentBlock';
import { FileCard } from './FileCard';

export interface ToolCallBlockProps {
  /** 包含工具信息的 ThinkingItem */
  readonly toolItem: ThinkingItem;
  readonly isStreaming?: boolean;
}

/**
 * 工具名称 → 友好名称映射
 */
const TOOL_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  'web_search': '网络搜索',
  'google_search': 'Google 搜索',
  'bing_search': 'Bing 搜索',
  'wikipedia': '维基百科',
  'code_interpreter': '代码执行',
  'dalle': '图片生成',
  'browser': '浏览网页',
  'arxiv': '论文搜索',
  'calculator': '计算器',
  'weather': '天气查询'
};

const getToolDisplayName = (tool: string): string =>
  TOOL_DISPLAY_NAMES[tool] ?? tool;

const getToolIcon = (tool: string): string => {
  if (tool.includes('search') || tool.includes('browser')) return 'fa-search';
  if (tool.includes('code') || tool.includes('interpreter')) return 'fa-code';
  if (tool.includes('dall') || tool.includes('image')) return 'fa-image';
  if (tool.includes('calc')) return 'fa-calculator';
  if (tool.includes('weather')) return 'fa-cloud-sun';
  if (tool.includes('wiki') || tool.includes('arxiv')) return 'fa-book';
  return 'fa-wrench';
};

export const ToolCallBlock: React.FC<ToolCallBlockProps> = memo(({
  toolItem,
  isStreaming = false
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const handleToggle = useCallback((): void => {
    setCollapsed(prev => !prev);
  }, []);

  const tool = toolItem.tool ?? 'unknown';
  const displayName = getToolDisplayName(tool);
  const iconClass = getToolIcon(tool);

  return (
    <div className={`tool-call-block ${collapsed ? 'collapsed' : 'expanded'}`}>
      <button
        type="button"
        className="tool-call-header"
        onClick={handleToggle}
        aria-expanded={!collapsed}
      >
        <span className="tool-call-icon">
          {isStreaming ? (
            <i className="fas fa-spinner fa-spin" />
          ) : (
            <i className={`fas ${iconClass}`} />
          )}
        </span>
        <span className="tool-call-label">
          {isStreaming ? `正在调用 ${displayName}...` : displayName}
        </span>
        <span className={`tool-call-chevron ${collapsed ? 'rotate' : ''}`}>
          <i className="fas fa-chevron-down" />
        </span>
      </button>

      {!collapsed && (
        <div className="tool-call-body">
          {toolItem.toolInput && (
            <div className="tool-call-section">
              <div className="tool-call-section-label">输入</div>
              <pre className="tool-call-section-content">{toolItem.toolInput}</pre>
            </div>
          )}
          {toolItem.observation && (
            <div className="tool-call-section">
              <div className="tool-call-section-label">输出</div>
              <pre className="tool-call-section-content">{toolItem.observation}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

ToolCallBlock.displayName = 'ToolCallBlock';